import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/20/solid'
import { Fragment, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { ChevronDownIcon } from '@ui/components/icons'
import { useTimeStore } from '@ui/stores'
import clsx from 'clsx'

interface Dropdown {
  english: string
  relative: [number, string]
}

const times: Dropdown[] = [
  { english: '1 min ago', relative: [1, 'minutes'] },
  { english: '5 min ago', relative: [5, 'minutes'] },
  { english: '10 min ago', relative: [10, 'minutes'] },
  { english: '15 min ago', relative: [15, 'minutes'] },
  { english: '20 min ago', relative: [20, 'minutes'] },
  { english: '30 min ago', relative: [30, 'minutes'] },
]

interface TimePickerProps {
  setParams?: () => void
  refetch?: () => void
}

export function TimePicker({ setParams, refetch }: TimePickerProps) {
  const setTimewindow = useTimeStore(useShallow((state) => state.setTimewindow))
  const timewindow = useTimeStore(useShallow((state) => state.timewindow))

  useEffect(() => {
    setTimewindow(timewindow)
    if (setParams == null || refetch == null) return
    setParams()
  }, [timewindow])

  return (
    <div className="h-full w-full">
      <Listbox value={timewindow} onChange={setTimewindow}>
        {({ open }) => (
          <>
            <div className="relative h-full">
              <ListboxButton
                className={
                  'relative h-full w-full cursor-pointer rounded-lg border border-gray-200 bg-white p-2 pl-3 pr-10 text-left text-sm focus:outline-none'
                }>
                <div className={'flex align-middle'}>
                  <span className="inline-flex h-full w-full truncate">
                    <span className="truncate">{timewindow.english}</span>
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon
                      className={clsx('h-5 w-5 stroke-gray-900 transition-all duration-300', {
                        'rotate-180': open,
                      })}
                      aria-hidden="true"
                    />
                  </span>
                </div>
              </ListboxButton>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0">
                <ListboxOptions className="absolute z-10 max-h-60 w-full overflow-auto rounded-b-sm border-x border-b border-gray-200 bg-white text-base focus:outline-none sm:text-sm">
                  {times.map((time) => (
                    <ListboxOption
                      key={time.english}
                      className={({ active }) =>
                        clsx(
                          {
                            'bg-primary-500/90 text-white': active,
                            'text-gray-900': !active,
                          },
                          'relative cursor-default select-none border-b border-gray-200 px-3 py-2'
                        )
                      }
                      value={time}>
                      {({ selected, active }) => (
                        <>
                          <div className="flex justify-between">
                            <span
                              className={clsx(
                                {
                                  'font-semibold': selected,
                                },
                                'font-normal'
                              )}>
                              {time.english}
                            </span>
                          </div>

                          {selected ? (
                            <span
                              className={clsx(
                                active ? 'text-white' : 'text-primary-500',
                                'absolute inset-y-0 right-0 flex items-center pr-4'
                              )}>
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  )
}
